import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import pallet from '../../../layouts/pallet';
import { StudentRollNoTableColumns } from '../utils/StudentRollNoTableColumn';
import { RollNoData } from '../types/rollNoDataType';
import { updateStudentRollNoApi } from '../../../apis/rollNoUpdate';
import { SnackbarSeverityType } from '../../../enums';
import { dataGridSx } from '../../common/styled/DataGrid';
import { ButtonWrapper, CancelButton, StyledButton } from '../styled';
import { downloadExcelFromJson } from '../../../utils/fileGeneration';
import { isEmpty } from 'lodash';
import tableColumnUtils from '../../../utils/tableColumnUtils';
import { PageWrapper } from '../../common/styled';
import CustomDataGridToolbar from '../../common/components/CustomDataGridToolbar';

const RollNumberUpdateTable = ({
  rollNoData,
  isSubmitting,
  showToast,
  onRollNoUpdateSuccess,
  handleSearch,
  setRollNoData
}: {
  rollNoData: RollNoData[];
  isSubmitting: boolean;
  setRollNoData: React.Dispatch<React.SetStateAction<RollNoData[]>>;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onRollNoUpdateSuccess: () => void;
  handleSearch: ({ displayToast }: { displayToast: boolean }) => Promise<void>;
}) => {
  const [editedRollNoData, setEditedRollNoData] =
    useState<RollNoData[]>(rollNoData);
  const [bulkRollNoUpdate, setBulkRollNoUpdate] = useState<
    Partial<RollNoData>[]
  >([]);
  const [originalStudentsList] = useState<RollNoData[]>(rollNoData);

  useEffect(() => {
    setEditedRollNoData(rollNoData);

    const studentsWithModifiedRollNumbers = rollNoData.filter((currentStudent) => {
      const originalStudent = originalStudentsList.find((initialStudent) => initialStudent.id === currentStudent.id);
      return originalStudent ? currentStudent.rollNo !== originalStudent.rollNo : false;
    });

    setBulkRollNoUpdate(
      studentsWithModifiedRollNumbers.map(({ email, rollNo }) => ({ email, rollNo }))
    );
  }, [rollNoData, originalStudentsList]);


  const onSubmit = async () => {
    if (rollNoData.length === 0) return;
    try {
      const response = await updateStudentRollNoApi(bulkRollNoUpdate);
      const updateResponse = response as any;
      if (!isEmpty(updateResponse.unSuccessfulRecords)) {
        const headers = ['email'];
        const body = updateResponse.unSuccessfulRecords.map((r: any) => [
          r.email
        ]);
        downloadExcelFromJson(
          'Unsuccessful_Roll_No_Update',
          'UnsuccessfulRollNoUpdate',
          { headers, body }
        );
      }
      showToast(
        'Successfully updated student roll no',
        SnackbarSeverityType.success
      );
      
      onRollNoUpdateSuccess();
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error updating roll no',
        SnackbarSeverityType.error
      );
    }
  };

  const onRollNoChange = (id: string, rollNo: string) => {
    setEditedRollNoData((data) => {
      const updatedRollNoData = data.map((currentStudent) =>
        currentStudent.id === id ? { ...currentStudent, rollNo } : currentStudent
      );
      const studentsWithModifiedRollNumbers = updatedRollNoData.filter((currentStudent) => {
        const originalStudent = originalStudentsList.find((initialStudent) => initialStudent.id === currentStudent.id);
        return originalStudent ? currentStudent.rollNo !== originalStudent.rollNo : false;
      });
      setBulkRollNoUpdate(
        studentsWithModifiedRollNumbers.map(({ email, rollNo }) => ({ email, rollNo }))
      );
      return updatedRollNoData;
    });
  };

  const onCancel = () => {
    setRollNoData(originalStudentsList);
    setEditedRollNoData(originalStudentsList);
    setBulkRollNoUpdate([]);
  };

  const StudentRollNoColumns = StudentRollNoTableColumns(onRollNoChange);
  return (
    <PageWrapper>
      {bulkRollNoUpdate.length > 0 && (
        <ButtonWrapper>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>

          <StyledButton sx={{ width: 'min-content' }} onClick={onSubmit}>
            Update
          </StyledButton>
        </ButtonWrapper>
      )}

      <DataGrid
        rowHeight={50}
        rows={editedRollNoData}
        columns={StudentRollNoColumns}
        loading={isSubmitting}
        disableRowSelectionOnClick
        pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
        initialState={{
          ...tableColumnUtils.DATA_GRID_INITIAL_STATE
        }}
        showToolbar
        slots={{
          toolbar: CustomDataGridToolbar
        }}
        sx={{
          ...dataGridSx(pallet),
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto'
          }
        }}
      />
    </PageWrapper>
  );
};

export default RollNumberUpdateTable;
